/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { isEmpty, isEqual } from 'lodash'
import React, { Component, Fragment } from 'react'
import { defineMessages, injectIntl } from 'react-intl'

import { EuiFilterButton, EuiIcon, EuiSkeletonText, EuiSpacer, EuiToolTip } from '@elastic/eui'

import type {
  DeploymentsSearchResponse,
  DeploymentSearchResponse,
  SearchRequest,
} from '@modules/cloud-api/v1/types'
import type {
  AsyncRequestState,
  CloudAppFamily,
  ProfileState,
  UserSubscription,
} from '@modules/ui-types'
import {
  getFilterQueryString,
  isEmptyQuery,
  setFilterQueryString,
} from '@modules/cui/FilterContext'
import { CuiAlert } from '@modules/cui/Alert'
import type { OnFilterChangeParams } from '@modules/cui/FilterContext'
import Header from '@modules/cui/Header'
import LocalStorageKey from '@modules/utils/localStorageKeys'
import { startPageActions } from '@modules/utils/apm'

import CreateStackDeploymentButton from '@/components/StackDeploymentSearch/CreateStackDeploymentButton'
import { deploymentsCrumbs } from '@/lib/crumbBuilder'
import { disableDeploymentButton, disableDeploymentCreation } from '@/lib/deployments/newDeployment'
import { isMarketPlaceUser, isUnSubscribedMarketPlaceUser } from '@/lib/marketPlace'
import { getConfigForKey } from '@/store'

import IlmTemplateMigrationCallout from '../IlmMigration/IlmTemplateMigrationCallout'
import UnSubscribedMarketPlaceUserCallout from '../MarketPlace/UnSubscribedMarketPlaceUserCallout'

import searchDeploymentsQuery from './searchDeploymentsQuery'
import TrialState from './TrialState'
import ExportDeploymentsModal from './ExportDeploymentsModal'
import DeploymentList from './DeploymentList'
import DeploymentFilterContext, { getEsQuery } from './DeploymentFilterContext'

import type { WrappedComponentProps } from 'react-intl'

type StateProps = {
  deletedDeploymentIds: string[]
  searchResults: DeploymentsSearchResponse | null
  searchResultsRequest: AsyncRequestState
  profile: ProfileState
  subscription?: UserSubscription
  hideCreateClusterButton: boolean
  exportDeployments: boolean
  showIlmMigrationCallout: boolean
  showTitle?: boolean
  showBreadcrumbs?: boolean
}

type DispatchProps = {
  searchDeployments: (query: SearchRequest) => Promise<void>
}

type ConsumerProps = {
  storageKey?: string
}

export type Props = StateProps & DispatchProps & ConsumerProps & WrappedComponentProps

type State = {
  esQuery: any
  query: string | null
  queryUsedForSearch: string | null
  queryResults: DeploymentSearchResponse[]
  showExportModal: boolean
  displayMode: 'grid' | 'table'
}

const messages = defineMessages({
  displayGrid: {
    id: `displayMode-grid`,
    defaultMessage: `Switch to grid view`,
  },
  displayTable: {
    id: `displayMode-table`,
    defaultMessage: `Switch to table view`,
  },
  export: {
    id: `export-deployments`,
    defaultMessage: `Export deployments...`,
  },
  title: {
    id: `hosted-deployments.title`,
    defaultMessage: `Hosted deployments`,
  },
  eceTitle: {
    id: `deployments.title`,
    defaultMessage: `Deployments`,
  },
})

const defaultEsQuery = {
  bool: {
    must: [
      {
        match: {
          stopped: { query: `n`, operator: `and` },
        },
      },
    ],
  },
}

class StackDeploymentSearch extends Component<Props, State> {
  startedTransaction = false

  static defaultProps = {
    storageKey: `StackDeploymentSearch`,
  }

  state: State = this.getInitialState()

  getInitialState(): State {
    const { storageKey } = this.props
    const queryString = getFilterQueryString({ storageKey })
    const esQuery = getEsQuery(queryString)
    const displayMode = getStoredDisplayMode()

    return {
      esQuery,
      query: queryString ?? null,
      queryResults: [],
      queryUsedForSearch: null,
      showExportModal: false,
      displayMode,
    }
  }

  componentDidMount() {
    this.search()
  }

  componentDidUpdate(_oldProps: Props, oldState: State) {
    const { searchResultsRequest, searchResults } = this.props
    const { esQuery } = this.state

    if (
      !this.startedTransaction &&
      (searchResultsRequest.error || (searchResults && searchResults.deployments))
    ) {
      this.startedTransaction = true

      startPageActions(`Deployment search`)
    }

    if (!isEqual(esQuery, oldState.esQuery)) {
      this.search()
    }
  }

  render() {
    const {
      hideCreateClusterButton,
      searchResults,
      profile,
      subscription,
      showIlmMigrationCallout,
      showTitle = true,
      showBreadcrumbs = true,
      intl: { formatMessage },
    } = this.props

    const deployments =
      searchResults && searchResults.deployments ? searchResults.deployments : null
    const hasDeployments = !isEmpty(deployments)
    const createDisabled = disableDeploymentCreation({ profile, hasDeployments })
    const trialUser = profile && profile.inTrial
    let restartTrial = false
    let disableCreateButton = false

    if (profile) {
      restartTrial = profile.canRestartTrial
      disableCreateButton = disableDeploymentButton({ profile })
    }

    const appFamily = getConfigForKey<CloudAppFamily>(`APP_FAMILY`)

    return (
      <div id='deployments'>
        {!subscription && trialUser && <TrialState deployments={deployments} profile={profile} />}

        {profile && isMarketPlaceUser(profile) && isUnSubscribedMarketPlaceUser(profile) && (
          <UnSubscribedMarketPlaceUserCallout profile={profile} />
        )}

        {showIlmMigrationCallout && <IlmTemplateMigrationCallout displayLink={true} />}

        {showTitle && (
          <Header
            name={
              appFamily === 'cloud-enterprise'
                ? formatMessage(messages.eceTitle)
                : formatMessage(messages.title)
            }
            breadcrumbs={showBreadcrumbs ? deploymentsCrumbs() : []}
            children={
              hasDeployments && (
                <CreateStackDeploymentButton
                  createDisabled={createDisabled}
                  disabled={disableCreateButton}
                  restartTrial={restartTrial}
                  hideCreateClusterButton={hideCreateClusterButton}
                  showCreateFirstDeployment={false}
                />
              )
            }
          />
        )}

        {this.renderContent()}
      </div>
    )
  }

  renderContent() {
    const {
      intl: { formatMessage },
      hideCreateClusterButton,
      profile,
      searchResults,
      searchResultsRequest,
      exportDeployments,
    } = this.props

    if (!searchResults) {
      return <EuiSkeletonText />
    }

    const { query, queryUsedForSearch, displayMode } = this.state

    const deployments = searchResults ? searchResults.deployments : []

    const isLoading = searchResultsRequest.inProgress
    const hasDeployments = !isEmpty(deployments)
    const showCreateFirstDeployment = isEmpty(deployments) && isDefaultEsQuery(queryUsedForSearch)
    const createDisabled = disableDeploymentCreation({ profile, hasDeployments })
    let restartTrial = false
    let disableCreateButton = false

    if (profile) {
      restartTrial = profile.canRestartTrial
      disableCreateButton = disableDeploymentButton({ profile })
    }

    if (!hideCreateClusterButton && showCreateFirstDeployment) {
      if (!searchResults && isLoading) {
        // show a loading spinner because of indeterminate state
        return <EuiSkeletonText />
      }

      return (
        <CreateStackDeploymentButton
          createDisabled={createDisabled}
          disabled={disableCreateButton}
          restartTrial={restartTrial}
          hideCreateClusterButton={hideCreateClusterButton}
          showCreateFirstDeployment={showCreateFirstDeployment && !profile?.hasExpiredTrial}
        />
      )
    }

    const tools = [
      displayMode === `table` ? (
        <EuiFilterButton
          key='display-mode-grid'
          css={{ width: `40px` }}
          onClick={this.toggleDisplayMode}
          aria-label={formatMessage(messages.displayGrid)}
        >
          <EuiToolTip content={formatMessage(messages.displayGrid)}>
            <EuiIcon type='grid' />
          </EuiToolTip>
        </EuiFilterButton>
      ) : (
        <EuiFilterButton
          key='display-mode-table'
          css={{ width: `40px` }}
          onClick={this.toggleDisplayMode}
          aria-label={formatMessage(messages.displayTable)}
        >
          <EuiToolTip content={formatMessage(messages.displayTable)}>
            <EuiIcon type='visTable' />
          </EuiToolTip>
        </EuiFilterButton>
      ),
    ]

    if (exportDeployments) {
      tools.unshift(
        <EuiFilterButton
          key='import-action'
          css={{ width: `40px` }}
          onClick={this.showExportModal}
          aria-label={formatMessage(messages.export)}
          data-test-id='deploymentExportButton'
        >
          <EuiToolTip content={formatMessage(messages.export)}>
            <EuiIcon type='importAction' />
          </EuiToolTip>
        </EuiFilterButton>,
      )
    }

    const filterContext = (
      <DeploymentFilterContext
        query={query}
        onChange={this.onChange}
        deployments={deployments}
        isLoading={isLoading}
        tools={tools}
      />
    )

    return (
      <Fragment>
        {filterContext}

        {searchResultsRequest.error && (
          <Fragment>
            <EuiSpacer size='l' />
            <CuiAlert type='error'>{searchResultsRequest.error}</CuiAlert>
          </Fragment>
        )}

        {this.renderDeploymentList()}
        {this.renderExportModal()}
      </Fragment>
    )
  }

  renderDeploymentList() {
    const { queryResults, displayMode, queryUsedForSearch } = this.state

    if (isEmpty(queryResults)) {
      return null
    }

    const { searchResults, deletedDeploymentIds } = this.props
    const { match_count = null, return_count = null } = searchResults || {}
    const deployments = queryResults.filter(
      (deployment) => !deletedDeploymentIds.includes(deployment.id),
    )
    const maintainOrder = !isDefaultEsQuery(queryUsedForSearch)

    return (
      <Fragment>
        <EuiSpacer size='l' />

        <DeploymentList
          displayMode={displayMode}
          deployments={deployments}
          shownCount={return_count}
          matchCount={match_count}
          reQuery={this.search}
          maintainOrder={maintainOrder}
        />
      </Fragment>
    )
  }

  renderExportModal() {
    const { queryResults = [], showExportModal } = this.state

    if (!showExportModal) {
      return null
    }

    return <ExportDeploymentsModal deployments={queryResults} close={this.hideExportModal} />
  }

  onChange = ({
    query,
    queryText,
    queryResults,
  }: OnFilterChangeParams<DeploymentSearchResponse>) => {
    const { storageKey } = this.props

    this.setState({
      esQuery: getEsQuery(query),
      query: queryText,
      queryResults,
    })

    setFilterQueryString({ storageKey, queryText })
  }

  search = ({ size }: { size?: number } = {}) => {
    const { searchDeployments, deletedDeploymentIds } = this.props
    const { esQuery } = this.state

    const query = searchDeploymentsQuery({
      esQuery,
      deletedDeploymentIds,
      size,
    })

    this.setState({ queryUsedForSearch: esQuery })

    return searchDeployments(query)
  }

  showExportModal = () => {
    this.search({ size: 2500 })
    this.setState({ showExportModal: true })
  }

  hideExportModal = () => {
    this.setState({ showExportModal: false })
  }

  toggleDisplayMode = () => {
    const { displayMode } = this.state
    const newDisplayMode = displayMode === `grid` ? `table` : `grid`

    this.setState({ displayMode: newDisplayMode })
    localStorage.setItem(LocalStorageKey.deploymentDisplayMode, newDisplayMode)
  }
}

export default injectIntl(StackDeploymentSearch)

function isDefaultEsQuery(query) {
  const emptyEsQuery = isEmptyQuery(query)

  if (emptyEsQuery) {
    return true
  }

  return isEqual(query, defaultEsQuery)
}

function getStoredDisplayMode(): 'grid' | 'table' {
  const displayMode = localStorage.getItem(LocalStorageKey.deploymentDisplayMode)

  if (displayMode != null && displayMode === 'table') {
    return 'table'
  }

  return 'grid'
}
